import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import adminApi from "../../../api/admin/adminApi";
import { ChatSummary } from "../../../api/admin/types";
import { AgentContentBlock, AgentMessage } from "./types";
import { StreamEvent, streamAgentTurn } from "./streamAgent";

function uuid(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }
  return "id-" + Math.random().toString(36).slice(2) + Date.now().toString(36);
}

export interface UseAdminAgent {
  // Currently-selected chat's transcript (finalized + in-progress).
  messages: AgentMessage[];
  isThinking: boolean;
  send: (text: string) => Promise<void>;

  // Chat list (recents) - shared across all super-admins.
  chats: ChatSummary[];
  chatsLoading: boolean;
  selectedChatId: string;
  selectChat: (chatId: string) => Promise<void>;
  newChat: () => void;
  deleteChat: (chatId: string) => Promise<void>;
  refreshChats: () => Promise<void>;
}

function appendTextDelta(blocks: AgentContentBlock[], text: string): AgentContentBlock[] {
  const last = blocks[blocks.length - 1];
  if (last && last.type === "text") {
    return [...blocks.slice(0, -1), { ...last, text: last.text + text }];
  }
  return [...blocks, { type: "text", text }];
}

export function useAdminAgent(): UseAdminAgent {
  // sessionId doubles as the chat_id - we generate a fresh one for "New chat"
  // and reuse it for the lifetime of that chat. The backend uses the same id
  // for the dataset cache, so handles persist alongside the chat.
  const [selectedChatId, setSelectedChatId] = useState<string>(() => uuid());
  const [messages, setMessages] = useState<AgentMessage[]>([]);
  const [streaming, setStreaming] = useState<AgentMessage | null>(null);
  const [isThinking, setIsThinking] = useState(false);
  const [chats, setChats] = useState<ChatSummary[]>([]);
  const [chatsLoading, setChatsLoading] = useState(false);
  const abortRef = useRef<AbortController | null>(null);
  // Live selected-chat id, read by deleteChat instead of its stale closure.
  const selectedChatIdRef = useRef(selectedChatId);
  useEffect(() => {
    selectedChatIdRef.current = selectedChatId;
  }, [selectedChatId]);
  // Bumped by send/selectChat/newChat; an in-flight call only commits its
  // result if this still matches the value it captured at start.
  const generationRef = useRef(0);

  const refreshChats = useCallback(async () => {
    setChatsLoading(true);
    try {
      const resp = await adminApi.adminListChats();
      if (resp.status < 400) {
        setChats(resp.body.chats || []);
      }
    } finally {
      setChatsLoading(false);
    }
  }, []);

  // Hydrate the chat list once on mount. We don't auto-load the most recent
  // chat - users start in a fresh chat and click into history if they want.
  useEffect(() => {
    refreshChats();
  }, [refreshChats]);

  const send = useCallback(
    async (text: string) => {
      const trimmed = text.trim();
      if (!trimmed || isThinking) return;
      const userMsg: AgentMessage = { role: "user", content: trimmed };
      const baseTranscript = [...messages, userMsg];
      const myGeneration = ++generationRef.current;
      setMessages(baseTranscript);
      setStreaming({ role: "assistant", content: [] });
      setIsThinking(true);

      const controller = new AbortController();
      abortRef.current = controller;

      let blocks: AgentContentBlock[] = [];
      // We capture the error message but don't push to messages until the
      // finally block, so the order ends up correct: assistant-with-partial,
      // then system-error.
      let errorMessage: string | null = null;

      const onEvent = (evt: StreamEvent) => {
        switch (evt.type) {
          case "text_delta":
            blocks = appendTextDelta(blocks, evt.text);
            setStreaming({ role: "assistant", content: blocks });
            break;
          case "tool_use":
            blocks = [
              ...blocks,
              { type: "tool_use", id: evt.id, name: evt.name, input: evt.input },
            ];
            setStreaming({ role: "assistant", content: blocks });
            break;
          case "tool_result":
            blocks = [
              ...blocks,
              { type: "tool_result", tool_use_id: evt.tool_use_id, content: evt.content },
            ];
            setStreaming({ role: "assistant", content: blocks });
            break;
          case "error":
            errorMessage = evt.message;
            break;
          case "done":
            break;
        }
      };

      try {
        await streamAgentTurn(
          { sessionId: selectedChatId, messages: baseTranscript, chatId: selectedChatId },
          { onEvent, signal: controller.signal }
        );
      } catch (e: unknown) {
        if ((e as Error).name !== "AbortError") {
          errorMessage = e instanceof Error ? e.message : "Network error.";
        }
      } finally {
        // Skip if a newer send/selectChat/newChat/deleteChat has taken over.
        if (generationRef.current === myGeneration) {
          // Even on error, preserve any blocks the agent streamed before
          // failing so the user sees what was already produced. Then append
          // the error as a system message after.
          setMessages((prev) => {
            const next = [...prev];
            if (blocks.length > 0) {
              next.push({ role: "assistant", content: blocks });
            }
            if (errorMessage) {
              next.push({ role: "system", content: errorMessage });
            }
            return next;
          });
          setStreaming(null);
          setIsThinking(false);
        }
        // Only clear the abort ref if it still points at this turn's controller.
        if (abortRef.current === controller) {
          abortRef.current = null;
        }
        // Refresh the sidebar even if the turn is stale - it's already persisted.
        refreshChats();
      }
    },
    [messages, isThinking, selectedChatId, refreshChats]
  );

  const selectChat = useCallback(
    async (chatId: string) => {
      if (chatId === selectedChatId) return;
      abortRef.current?.abort();
      abortRef.current = null;
      const myGeneration = ++generationRef.current;
      setSelectedChatId(chatId);
      setMessages([]);
      setStreaming(null);
      setIsThinking(false);
      try {
        const resp = await adminApi.adminGetChat(chatId);
        // Discard if stale: overtaken by another selectChat, or by a send() into this chat.
        if (resp.status < 400 && generationRef.current === myGeneration) {
          setMessages(resp.body.messages || []);
        }
      } catch {
        /* swallow - chat list will reflect any failure */
      }
    },
    [selectedChatId]
  );

  const newChat = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    generationRef.current++;
    setSelectedChatId(uuid());
    setMessages([]);
    setStreaming(null);
    setIsThinking(false);
  }, []);

  const deleteChatHandler = useCallback(
    async (chatId: string) => {
      try {
        await adminApi.adminDeleteChat(chatId);
      } catch {
        /* ignore */
      }
      // If the deleted chat is still open, drop into a fresh one. Uses the
      // live ref since the user may have switched chats mid-delete.
      if (chatId === selectedChatIdRef.current) {
        newChat();
      }
      refreshChats();
    },
    [newChat, refreshChats]
  );

  const displayMessages = useMemo(
    () => (streaming ? [...messages, streaming] : messages),
    [messages, streaming]
  );

  return {
    messages: displayMessages,
    isThinking,
    send,
    chats,
    chatsLoading,
    selectedChatId,
    selectChat,
    newChat,
    deleteChat: deleteChatHandler,
    refreshChats,
  };
}
